import { computed, effect, inject, untracked } from "@angular/core";
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from "@ngrx/signals";
import { AdvancedSearchUtil } from "../modules/advanced-search/advanced-search-util";
import { S25QLModeller } from "../modules/s25-ql/s25ql.modeller";
import { AdvancedModel, AdvancedStep } from "../modules/s25-ql/s25ql.search.advanced.criteria.component";
import { S25QLTokenizer } from "../modules/s25-ql/s25ql.tokenizer";
import { QLUtil } from "../modules/s25-ql/s25ql.util";
import { SimpleSearch } from "../modules/s25-ql/simple.search.criteria";
import { DropDownItem } from "../pojo/DropDownItem";
import { Fls } from "../pojo/Fls";
import { Proto } from "../pojo/Proto";
import { Report } from "../pojo/Report";
import { SearchCriteria } from "../pojo/SearchCriteria";
import { Nullable } from "../pojo/Util";
import { ContactService } from "../services/contact.service";
import { ReportService } from "../services/report.service";
import { SearchService } from "../services/search/search.service";
import { TaskCalendarService } from "../services/task/task.calendar.service";
import { CalendarOptModelBean } from "../standalone/calendar/calendar.with.options.component";
import { S25Const } from "../util/s25-const";
import { S25Util } from "../util/s25-util";
import { S25Assert } from "../util/s25.assert";
import { s25Resource } from "../util/s25.resource";
import { FlsStore } from "./fls.store";
import { LoginStore } from "./login.store";
import { Item } from "../pojo/Item";
import { ComplexSearch, KeywordCriteria, SavedSearch, Search } from "../modules/s25-ql/search";
import StepTypeId = SearchCriteria.StepTypeId;
import { SignalUtil } from "../util/signal.util";

type SearchPerm = {
    simple: boolean;
    def: boolean;
    adv: boolean;
};

type SearchState = {
    tab: Search.Tab;
    /**
     * The working search, i.e. text field, advanced search, or a chosen saved search
     */
    search?: Search;
    pristine?: boolean;
    fromSavedSearch?: DropDownItem<number>;
    loadingSavedSearchCriteria?: SignalUtil.FromPromise<void>;
    // TODO: get rid of this
    calBean: CalendarOptModelBean;
    errorMessage?: Nullable<string>;
};

type State = {
    stateByType: { [id in Search.TypeId]: SearchState };
    _internalTypeId: Search.TypeId | undefined;
    _reportSuccess: { [id in Search.TypeId]?: boolean };
};

const initialState = <{ [id in Search.TypeId]: SearchState }>Object.fromEntries(
    Search.allTypeIds.map((typeId): [Search.TypeId, SearchState] => [
        typeId,
        {
            tab: "list",
            calBean: {
                // TODO: make these inputs on calendar, or a directive that exposes a store, or something......
                optBeanRunTime: {
                    hasListVisualization: true,
                    hasCalendarVisualization: [
                        Item.Ids.Event,
                        Item.Ids.Organization,
                        Item.Ids.Location,
                        Item.Ids.Resource,
                        Item.Ids.Task,
                    ].includes(typeId),
                    hasAvailabilityVisualization: [Item.Ids.Event, Item.Ids.Location, Item.Ids.Resource].includes(
                        typeId,
                    ),
                    hasAvailabilityWeeklyVisualization: typeId === Item.Ids.Location,
                    selectedViz: "calendar", // eyeroll
                    hasRelatedLocationsCheckbox: typeId === Item.Ids.Location,
                },
                ...(typeId === Item.Ids.Task ? { getdata: TaskCalendarService.getCalendar } : {}),
            },
        },
    ]),
);

const reportMap = {
    [Item.Ids.Event]: { rpt_id: Report.Reports.EventListing.id, paramName: "ev_query_id" },
    [Item.Ids.Organization]: { rpt_id: Report.Reports.OrganizationListing.id, paramName: "ac_query_id" },
    [Item.Ids.Contact]: { rpt_id: 0 },
    [Item.Ids.Location]: { rpt_id: Report.Reports.LocationListing.id, paramName: "rm_query_id" },
    [Item.Ids.Resource]: { rpt_id: Report.Reports.ResourceListing.id, paramName: "rs_query_id" },
    [Item.Ids.Task]: { rpt_id: Report.Reports.TaskListing.id },
} as const satisfies Record<Search.TypeId, { rpt_id: number; paramName?: string }>;

export const SearchStore = signalStore(
    { providedIn: "root" },
    withState<State>({
        stateByType: initialState,
        _internalTypeId: undefined,
        _reportSuccess: {},
    }),
    withProps(() => ({
        _fls: inject(FlsStore) as FlsStore,
        _login: inject(LoginStore) as LoginStore,
        // TODO: put these in a ContactStore or something
        _contactName: s25Resource(() => ContactService.getCurrentName()),
        _contactId: s25Resource(() => ContactService.getCurrentId()),
    })),
    withComputed((store) => {
        const typeId = computed(() => store._internalTypeId() ?? Item.Ids.Event);
        const typeName = computed((): Search.Type => S25Const.itemId2Name[typeId()]);
        const state = computed((): SearchState => store.stateByType()[typeId()]);
        const search = computed(() => state().search);
        const savedSearch = computed((): SavedSearch | undefined => {
            const currentSearch = search();
            if (Search.isSaved(currentSearch)) {
                return currentSearch;
            }
        });
        const fromSavedSearch = computed(() => state().fromSavedSearch);
        const criteria = computed(() => keywordSearch()?.criteria);
        const searchPermsByItemType = computed(() => ({
            [Item.Ids.Event]: eventSearchPerms(store._fls, store._login.loggedIn()),
            [Item.Ids.Location]: locationSearchPerms(store._fls, store._login.loggedIn()),
            [Item.Ids.Organization]: organizationSearchPerms(store._fls, store._login.loggedIn()),
            [Item.Ids.Resource]: resourceSearchPerms(store._fls, store._login.loggedIn()),
            [Item.Ids.Contact]: contactSearchPerms(store._fls, store._login.loggedIn()),
            [Item.Ids.Task]: taskSearchPerms(store._fls, store._login.loggedIn()),
        }));
        const allButtonPerms = computed(
            (): Record<Search.TypeId, ButtonPerms> => ({
                [Item.Ids.Event]: ButtonPerms.calculate({
                    store,
                    isOwner,
                    typeId: Item.Ids.Event,
                    search: "EVENT_SEARCH",
                    bulkEdit: "WEBSECURITY",
                }),
                [Item.Ids.Organization]: ButtonPerms.calculate({
                    store,
                    isOwner,
                    typeId: Item.Ids.Organization,
                    create: "CU_ACCOUNT",
                    search: "ACCOUNT_SEARCH",
                    bulkEdit: "CU_ACCOUNT",
                }),
                [Item.Ids.Contact]: ButtonPerms.calculate({
                    store,
                    isOwner,
                    typeId: Item.Ids.Contact,
                    create: "CU_CONTACT",
                    search: "CONTACT_SEARCH",
                }),
                [Item.Ids.Location]: ButtonPerms.calculate({
                    store,
                    isOwner,
                    typeId: Item.Ids.Location,
                    create: "SPACE_LIST",
                    search: "SPACE_SEARCH",
                    bulkEdit: "SPACE_LIST",
                }),
                [Item.Ids.Resource]: ButtonPerms.calculate({
                    store,
                    isOwner,
                    typeId: Item.Ids.Resource,
                    create: "RESOURCE_LIST",
                    search: "RESOURCE_SEARCH",
                    bulkEdit: "RESOURCE_LIST",
                }),
                [Item.Ids.Task]: ButtonPerms.calculate({
                    store,
                    isOwner,
                    typeId: Item.Ids.Task,
                    search: "TASK_LIST",
                    bulkEdit: "TASK_LIST",
                }),
            }),
        );
        const allowedItemTypes = computed(() =>
            Search.allTypeIds.filter((opt) => {
                const p = searchPermsByItemType()[opt];
                return p.adv || p.def || p.simple;
            }),
        );
        const searchPerms = computed(() => searchPermsByItemType()[typeId()]);
        const isOwner = computed((): boolean => !!fromSavedSearch()?.isOwner);
        const rawQLSearch = computed(() => {
            const input = search();
            if (Search.isQl(input)) {
                return input;
            }
        });
        const advancedSearch = computed(() => {
            const input = search();
            if (Search.isAdvanced(input)) {
                return input;
            }
        });
        const criteriaAsSteps = computed(() =>
            SimpleSearch.Criteria.asSteps(criteria(), typeId(), {
                id: store._contactId.value(),
                name: store._contactName.value(),
            }),
        );
        const keywordSearch = computed(() => {
            const input = search();
            if (Search.isKeyword(input)) {
                return input;
            }
        });
        const complexSearch = computed(() => getComplexSearch());
        const qlSearch = computed(() => {
            const _qlSearch = rawQLSearch();
            if (_qlSearch) {
                const [data, error] = S25QLTokenizer.validateQL(_qlSearch.input, typeId());
                return {
                    data: data
                        ? {
                              ...data,
                              model: {
                                  ...data.model,
                                  query_method: data.model.query_method ?? "all",
                              },
                          }
                        : undefined,
                    error,
                };
            }
        });
        const criteriaParams = computed(() =>
            criteria() ? SimpleSearch.Criteria.serialize(criteria()!, typeId()) : "",
        );
        const searchHasValue = computed(() => {
            if (!!keywordSearch()) {
                return !!(keywordSearch()?.input?.length || criteriaParams()?.length);
            } else if (!!rawQLSearch()) {
                return true;
            } else if (advancedSearch()) {
                return !!(
                    advancedSearch()?.model?.step?.length || Object.entries(advancedSearch()?.searches ?? {}).length
                );
            } else {
                return !!savedSearch();
            }
        });
        return {
            _state: state,
            typeId,
            typeName,
            tab: computed(() => state().tab),
            initialized: computed(() => store._fls.loaded() && store._login.init()),
            search,
            advancedSearch,
            keywordSearch,
            rawQLSearch,
            criteria,
            savedSearch,
            fromSavedSearch,
            isOwner,
            report: computed(() => reportMap[typeId()]),
            qlSearch,
            text: computed(() => {
                const input = search();
                if (S25Util.isDefined(input) && "input" in input) {
                    return input.input ?? "";
                }
                return "";
            }),
            errorMessage: computed(() => state().errorMessage),
            keywordParam: computed(() =>
                keywordSearch()?.input ? "&name=" + encodeURIComponent(keywordSearch()!.input!) : "",
            ),
            criteriaParams,
            criteriaAsSteps,
            allowedItemTypes,
            searchPermsByItemType,
            searchPerms,
            canAdvancedSearch: computed(() => searchPerms().adv),
            subjectOptions: computed((): { id: Search.TypeId; name: Search.Type }[] =>
                allowedItemTypes().map((typeId) => ({
                    id: typeId,
                    name: S25Const.itemId2Name[typeId],
                })),
            ),
            buttonPerms: computed(() => allButtonPerms()[typeId()]),
            securityLink: computed(() => {
                if (!store._login.loggedIn() || !store._fls.hasRead("CU_CONTACT") || !store._fls.hasEdit("SECURITY"))
                    return false;
                switch (typeId()) {
                    case Item.Ids.Event:
                        return store._fls.hasFull("EVENT_PERM") || store._fls.hasFull("EVENT_SECURITY");
                    case Item.Ids.Organization:
                        return store._fls.hasFull("CU_PERM") || store._fls.hasFull("ACCOUNT_SECURITY");
                    case Item.Ids.Location:
                        return store._fls.hasFull("SPACE_PERM") || store._fls.hasFull("SPACE_SECURITY");
                    case Item.Ids.Resource:
                        return store._fls.hasFull("RESOURCE_PERM") || store._fls.hasFull("RESOURCE_SECURITY");
                    default:
                        return false;
                }
            }),
            complexSearch,
            searchHasValue,
            searchIsDirty: computed(() => !state().pristine && searchHasValue()),
            isKeywordSearching: computed(() => !search() || !!keywordSearch()),
            searchAsQL: computed(() => {
                const complex = complexSearch();
                if (!complex) {
                    return false;
                }
                return S25QLModeller.serialize(typeId(), complex.model, complex.searches) || "";
            }),
            loadingSavedSearchCriteria: () => state().loadingSavedSearchCriteria,
            isPredefinedSearch: () => fromSavedSearch()?.type === "predef",
        };

        function getComplexSearch(): ComplexSearch {
            let model: ComplexSearch | undefined;
            if (savedSearch()) {
                model = savedSearch()!.complex;
            } else if (advancedSearch()) {
                model = advancedSearch()!;
                // TODO: this mutates the advanced search.... that seems bad??
                const steps = S25Util.array.flatten([
                    model.model.step,
                    Object.values(model.searches).map((s: any) => s.step),
                ]);
                for (const step of steps) {
                    // what is this doing?
                    const key = step.step_param?.[0]?.itemName;
                    if (!QLUtil.isStepTemp(step) || !(key in model.searches) || model.searches[key].step.length > 1)
                        continue;
                    Object.assign(S25Util.clearObject(step), model.searches[key].step[0]);
                    delete model.searches[key];
                }
            } else if (qlSearch()) {
                model = qlSearch()!.data;
            } else if (typeId() !== Item.Ids.Task) {
                let steps = criteriaAsSteps();
                if (keywordSearch()?.input) {
                    const stepTypeId = (typeId() * 100 + 45) as StepTypeId; // Keyword is X45
                    const keywordStep = AdvancedSearchUtil.getStep(typeId(), stepTypeId);
                    keywordStep.step_param[0].itemName = keywordSearch()!.input;
                    steps = [keywordStep, ...steps];
                }
                return { model: { query_method: "all", step: [...steps] }, searches: {} };
            }
            if (!model) {
                return { model: { query_method: "all", step: [] }, searches: {} };
            } else {
                return model;
            }
        }
    }),
    withMethods((store) => {
        return {
            /**
             * Returns false if the type is not allowed due to permissions
             * @param type
             */
            setItemType: (type: Search.TypeId) => {
                if (store.initialized() && !store.allowedItemTypes().includes(type)) {
                    console.warn(`SearchStore: attempt to set disallowed type ${type}`);
                    return false;
                }
                patchState(store, { _internalTypeId: type });
                return true;
            },
            updateItemState: _updateItemState,
            reset: () => {
                _updateItemState({
                    search: undefined,
                    fromSavedSearch: undefined,
                    errorMessage: undefined,
                    pristine: true,
                });
            },
            clearError: () => _updateItemState({ errorMessage: undefined }),
            setError: _setError,
            setTextInput: _setTextInput,
            setCriteria: _setCriteria,
            validateKeywordSearch: () => {
                const keywordSearch = store.keywordSearch();
                if (keywordSearch) {
                    let error;
                    if (store.typeId() === Item.Ids.Task) {
                        error =
                            "Keyword search is not available for tasks. Please use SeriesQL or switch to Advanced mode.";
                    } else if (
                        (!keywordSearch.input || keywordSearch.input.length < 2) &&
                        !store.criteriaParams().length
                    ) {
                        error = "Keyword search must contain at least 2 characters.";
                    } else if (keywordSearch.input && keywordSearch.input.length > 48) {
                        error = "Keyword searches may not exceed 48 characters.";
                    }
                    if (error) {
                        _setError(error);
                        return false;
                    }
                }
                return true;
            },
            validateQLSearch: () => {
                let error;
                const qlSearch = store.qlSearch();
                if (qlSearch?.error) {
                    error = qlSearch.error;
                } else if (qlSearch?.data) {
                    error = S25QLModeller.validate(store.typeId(), qlSearch.data.model, qlSearch.data.searches);
                } else {
                    error = "No Search provided";
                }
                if (error) {
                    _setError(error);
                    return false;
                }
                return true;
            },
            /**
             * This will immediately select the saved search, then send an async request to get the complex form.
             * If you need the complex value to be populated in the store, wait for the promise result
             */
            chooseSavedSearch: async (savedSearch: Nullable<DropDownItem<number>>) => {
                S25Assert.isDefined(savedSearch);
                const existing = store.savedSearch()?.chosen;
                if (savedSearch === existing || DropDownItem.hash(savedSearch) === DropDownItem.hash(existing)) {
                    return;
                }
                const searchValue = {
                    chosen: savedSearch,
                    input: savedSearch.itemName!,
                };
                _updateItemState({
                    search: searchValue,
                    fromSavedSearch: savedSearch,
                    pristine: true,
                });
                if (savedSearch.itemId && S25Util.isNumeric(savedSearch.itemId) && store._login.loggedIn()) {
                    const promise = SearchService.getFullSearchCriteria(store.typeId(), savedSearch.itemId);
                    _updateItemState({
                        loadingSavedSearchCriteria: SignalUtil.fromPromise(promise.then()),
                    });
                    return promise.then((complex) => {
                        const currentSavedSearch = store.savedSearch();
                        if (savedSearch === currentSavedSearch?.chosen) {
                            _updateItemState({
                                search: {
                                    ...searchValue,
                                    complex,
                                },
                                pristine: true,
                            });
                        }
                    });
                }
            },
            convertToQL: () => {
                try {
                    const text = store.searchAsQL();
                    if (!text || /^[\s\n\t]*::[\s\n\t]*$/i.test(text)) {
                        _setTextInput("");
                    } else {
                        // maintain the pristine value, because converting does not modify the search
                        _setTextInput(text, { pristine: store._state().pristine });
                    }
                    return true;
                } catch (error: any) {
                    _setError(error.message);
                    return false;
                }
            },
            convertToAdvanced: async () => {
                try {
                    const complexSearch = store.complexSearch();
                    _updateItemState({
                        search: {
                            advanced: true,
                            model: <AdvancedModel>complexSearch.model,
                            searches: complexSearch.searches,
                        },
                        // maintain the pristine value, because converting does not modify the search
                        pristine: store._state().pristine,
                    });
                } catch (error: any) {
                    _setError(error.message);
                }
            },
        };

        function _updateItemState(update: Partial<SearchState> & { typeId?: Search.TypeId }): boolean {
            let updated = false;
            patchState(store, (state) => {
                const newState = handleStatePatch(state, update);
                updated = newState !== state;
                return newState;
            });
            return updated;
        }

        function handleStatePatch(state: State, update: Partial<SearchState> & { typeId?: Search.TypeId }): State {
            const typeId = update.typeId ?? state._internalTypeId;
            if (!typeId) {
                console.warn("no type ID set yet");
                return state;
            }
            const oldItemState = state.stateByType[typeId];
            // avoid creating a new state object if none of the updates actually change anything
            // note: this does mean in-place mutated objects will not trigger state updates
            if (
                Object.keys(update)
                    .filter((key): key is keyof SearchState => key !== "typeId")
                    .every((key) => oldItemState[key] === update[key])
            ) {
                return state;
            }
            const newItemState: SearchState = {
                ...oldItemState,
                ...update,
            };
            // reset the error message if they clear search or start trying to fix it
            if (!update.errorMessage && "input" in update) {
                newItemState.errorMessage = undefined;
            }
            if ("search" in update && !("pristine" in update)) {
                newItemState.pristine = false;
            }
            if (
                (update.tab === "availabilityWeekly" && typeId !== Item.Ids.Location) ||
                (update.tab === "availability" &&
                    ![Item.Ids.Event, Item.Ids.Location, Item.Ids.Resource].includes(typeId))
            ) {
                newItemState.tab = "list";
            }
            return {
                ...state,
                stateByType: {
                    ...state.stateByType,
                    [typeId]: newItemState,
                },
            };
        }

        function _setTextInput(text: string | undefined, options: { pristine?: boolean } = {}) {
            const oldCriteria = untracked(store.keywordSearch)?.criteria;
            if (!text && !oldCriteria) {
                return _updateItemState({ search: undefined, pristine: true });
            }
            const isQL = !!text && S25QLTokenizer.isQL(text) && untracked(store.canAdvancedSearch);
            return _updateItemState({
                search: isQL ? { ql: true, input: text } : { input: text, criteria: oldCriteria },
                pristine: options.pristine ?? false,
            });
        }

        function _setCriteria(criteria?: KeywordCriteria) {
            const keywordSearch = untracked(store.keywordSearch);
            if (keywordSearch || Object.values(criteria ?? {}).filter(S25Util.isDefined).length) {
                return _updateItemState({
                    search: {
                        input: keywordSearch?.input,
                        criteria: criteria,
                    },
                });
            } else {
                return false;
            }
        }

        function _setError(message?: string) {
            return _updateItemState({ errorMessage: message });
        }
    }),
    withHooks((store) => ({
        onInit: () => {
            effect(() => {
                const allowedTypes = store.allowedItemTypes();
                const typeId = store._internalTypeId();
                if (allowedTypes.length && (!typeId || !allowedTypes.includes(typeId))) {
                    if (typeId && typeId !== allowedTypes[0]) {
                        console.warn(
                            `Reverting search type from ${typeId} to ${allowedTypes[0]} due to permission change.`,
                        );
                    }
                    store.setItemType(allowedTypes[0]);
                }
            });
            effect(async () => {
                if (store.buttonPerms().runReport === "unverified") {
                    const itemTypeId = store.typeId();
                    // try fetching the report to ensure we have access
                    try {
                        const fetchSuccess = !!(await ReportService.getReport(store.report().rpt_id))?.report_id;
                        patchState(store, {
                            _reportSuccess: {
                                ...store._reportSuccess(),
                                [itemTypeId]: fetchSuccess,
                            },
                        });
                    } catch (e) {
                        patchState(store, {
                            _reportSuccess: {
                                ...store._reportSuccess(),
                                [itemTypeId]: false,
                            },
                        });
                    }
                }
            });
        },
    })),
);

export type SearchStore = InstanceType<typeof SearchStore>;

function taskSearchPerms(flsStore: FlsStore, loggedIn: boolean): SearchPerm {
    return {
        // no simple search for tasks
        simple: false,
        def: flsStore.hasRead("TASK_LIST"),
        adv: loggedIn && flsStore.hasFull("TASK_LIST"),
    };
}

function contactSearchPerms(flsStore: FlsStore, loggedIn: boolean): SearchPerm {
    const canRead = flsStore.hasRead("CU_CONTACT") && flsStore.hasRead("CONTACT_SEARCH");
    return {
        simple: canRead,
        def: canRead,
        adv: loggedIn && canRead && flsStore.hasFull("CONTACT_SEARCH"),
    };
}

function resourceSearchPerms(flsStore: FlsStore, loggedIn: boolean): SearchPerm {
    const canRead =
        flsStore.hasRead("RESOURCE_SEARCH") && (flsStore.hasRead("RESOURCE_LIST") || flsStore.hasFull("RESOURCE_PERM"));
    return {
        simple: canRead,
        def: canRead,
        adv: loggedIn && canRead && flsStore.hasFull("RESOURCE_SEARCH"),
    };
}

function organizationSearchPerms(flsStore: FlsStore, loggedIn: boolean): SearchPerm {
    const canRead =
        flsStore.hasRead("ACCOUNT_SEARCH") && (flsStore.hasRead("CU_ACCOUNT") || flsStore.hasFull("CU_PERM"));
    return { simple: canRead, def: canRead, adv: loggedIn && canRead && flsStore.hasFull("ACCOUNT_SEARCH") };
}

function locationSearchPerms(flsStore: FlsStore, loggedIn: boolean): SearchPerm {
    const canRead =
        flsStore.hasRead("SPACE_SEARCH") && (flsStore.hasRead("SPACE_LIST") || flsStore.hasFull("SPACE_PERM"));
    return { simple: canRead, def: canRead, adv: loggedIn && canRead && flsStore.hasFull("SPACE_SEARCH") };
}

function eventSearchPerms(flsStore: FlsStore, loggedIn: boolean): SearchPerm {
    const canRead =
        flsStore.hasRead("EVENT_SEARCH") && (flsStore.hasRead("EVENT_EVS") || flsStore.hasFull("EVENT_PERM"));
    return { simple: canRead, def: canRead, adv: loggedIn && canRead && flsStore.hasFull("EVENT_SEARCH") };
}

type ButtonPerms = Partial<
    Record<
        | "create"
        | "edit"
        | "save"
        | "saveAs"
        | "saveOrSaveAs"
        | "rename"
        | "refresh"
        | "share"
        | "delete"
        | "publish"
        | "bulkEdit"
        | "runReport",
        boolean | "unverified" | "verified"
    >
>;
namespace ButtonPerms {
    export function calculate({
        store: { _fls, _login, _reportSuccess },
        isOwner,
        search,
        create,
        typeId,
        bulkEdit,
    }: {
        store: { _fls: FlsStore; _login: LoginStore; _reportSuccess: () => State["_reportSuccess"] };
        isOwner: () => boolean;
        typeId: Search.TypeId;
        search: keyof Fls;
        create?: keyof Fls;
        bulkEdit?: keyof Fls;
    }): ButtonPerms {
        if (!_login.loggedIn()) {
            return {};
        }
        const _isOwner = isOwner();
        const searchEdit = _fls.hasEdit(search);
        const searchFull = _fls.hasFull(search);
        const reportFls = typeId !== Item.Ids.Contact && _fls.hasRead("REP_LIST");
        return {
            create: !!create && _fls.hasFull(create),
            edit: _isOwner && searchEdit,
            save: _isOwner && searchEdit,
            saveAs: searchEdit,
            saveOrSaveAs: searchEdit,
            rename: _isOwner && searchEdit,
            share: typeId !== Item.Ids.Contact && _fls.hasRead("SECURITY") && _fls.hasRead("CU_CONTACT"),
            delete: _isOwner && searchEdit,
            publish: [Item.Ids.Event, Item.Ids.Location].includes(typeId) && searchFull && _fls.hasFull("VCAL_PUB"),
            bulkEdit: !!bulkEdit && (typeId === Item.Ids.Event ? _fls.hasFull(bulkEdit) : _fls.hasEdit(bulkEdit)),
            runReport:
                !reportFls || _reportSuccess()[typeId] === false
                    ? false
                    : _reportSuccess()[typeId] === true
                      ? "verified"
                      : // reportSuccess is undefined
                        "unverified",
        };
    }
}
